/* ==========================================================================
   Casa Esmeralda — storefront behaviour (vanilla JS, no dependencies)
   ========================================================================== */
(() => {
  'use strict';

  const CE = window.CasaEsmeralda || { routes: {}, strings: {} };
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------- Money ---------- */

  function formatMoney(cents, format = CE.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return frac ? withSep + decimal + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return fmt(value, 0);
        case 'amount_with_comma_separator':
          return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator':
          return fmt(value, 2, ' ', ',');
        default:
          return fmt(value, 2);
      }
    });
  }

  /* ---------- Palette toggle ---------- */

  function initPalette() {
    const root = document.documentElement;
    const sync = () => {
      qsa('[data-palette-toggle]').forEach((btn) => {
        btn.setAttribute('aria-checked', String(root.dataset.theme === 'dermaskin'));
      });
    };
    sync();
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-palette-toggle]');
      if (!btn) return;
      const next = root.dataset.theme === 'dermaskin' ? 'esmeralda' : 'dermaskin';
      root.dataset.theme = next;
      try {
        localStorage.setItem('ce-palette', next);
      } catch (err) {
        /* storage unavailable */
      }
      sync();
    });
  }

  /* ---------- Header ---------- */

  function initHeader() {
    const header = qs('[data-header]');
    if (!header) return;

    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Dropdowns: hover on pointer devices, focus for keyboard users
    qsa('[data-dropdown]', header).forEach((item) => {
      const trigger = qs('.nav__link', item);
      let closeTimer;
      const open = () => {
        clearTimeout(closeTimer);
        qsa('[data-dropdown].is-open', header).forEach((o) => o !== item && close(o));
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = (el = item) => {
        el.classList.remove('is-open');
        qs('.nav__link', el)?.setAttribute('aria-expanded', 'false');
      };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => close(), 150);
      });
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) close();
      });
      item.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          trigger.focus();
        }
      });
    });

    // Mobile menu
    const menuBtn = qs('[data-menu-toggle]', header);
    const menu = qs('#MobileMenu', header);
    if (menuBtn && menu) {
      menuBtn.addEventListener('click', () => {
        const isOpen = menuBtn.getAttribute('aria-expanded') === 'true';
        menuBtn.setAttribute('aria-expanded', String(!isOpen));
        menu.hidden = isOpen;
        qs('[data-icon-open]', menuBtn).hidden = !isOpen;
        qs('[data-icon-close]', menuBtn).hidden = isOpen;
      });
    }

    // Search panel
    const searchBtn = qs('[data-search-toggle]', header);
    const searchPanel = qs('#HeaderSearch', header);
    if (searchBtn && searchPanel) {
      const closeSearch = () => {
        searchPanel.hidden = true;
        searchBtn.setAttribute('aria-expanded', 'false');
      };
      searchBtn.addEventListener('click', () => {
        const willOpen = searchPanel.hidden;
        searchPanel.hidden = !willOpen;
        searchBtn.setAttribute('aria-expanded', String(willOpen));
        if (willOpen) qs('input[type="search"]', searchPanel)?.focus();
      });
      searchPanel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeSearch();
          searchBtn.focus();
        }
      });
      document.addEventListener('click', (e) => {
        if (!searchPanel.hidden && !searchPanel.contains(e.target) && !searchBtn.contains(e.target)) closeSearch();
      });
    }

    initPredictiveSearch(header);
  }

  function initPredictiveSearch(root) {
    const form = qs('[data-predictive-search]', root);
    if (!form || !CE.routes.predictiveSearch) return;
    const input = qs('input[type="search"]', form);
    const results = qs('[data-predictive-results]', form);
    let controller;

    const escapeHtml = (str) =>
      String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

    const render = (products, term) => {
      if (!products.length) {
        results.innerHTML = '';
        return;
      }
      const items = products
        .map((p) => {
          const img = p.featured_image?.url
            ? `<img src="${p.featured_image.url}${p.featured_image.url.includes('?') ? '&' : '?'}width=120" alt="" width="44" height="44" loading="lazy">`
            : '';
          return `<a class="predictive__item" href="${p.url}">${img}<span>${escapeHtml(p.title)}<span class="predictive__price">${formatMoney(
            Math.round(parseFloat(p.price) * 100)
          )}</span></span></a>`;
        })
        .join('');
      const allUrl = `${form.action}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      results.innerHTML = `${items}<a class="predictive__all" href="${allUrl}">${escapeHtml(term)} →</a>`;
    };

    input.addEventListener(
      'input',
      debounce(async () => {
        const term = input.value.trim();
        if (term.length < 2) {
          results.innerHTML = '';
          return;
        }
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${CE.routes.predictiveSearch}.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error(res.status);
          const data = await res.json();
          render(data.resources?.results?.products || [], term);
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        }
      }, 250)
    );
  }

  /* ---------- Tabs ---------- */

  function initTabs(scope = document) {
    qsa('[data-tabs]', scope).forEach((wrap) => {
      const tabs = qsa('[role="tab"]', wrap);
      if (!tabs.length) return;
      const heading = qs('[data-tab-heading]', wrap);
      const select = (tab, focus = false) => {
        tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        if (heading && tab.dataset.heading) heading.textContent = tab.dataset.heading;
        if (focus) tab.focus();
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') select(tabs[(i + 1) % tabs.length], true);
          if (e.key === 'ArrowLeft') select(tabs[(i - 1 + tabs.length) % tabs.length], true);
        });
      });
    });
  }

  /* ---------- Cart ---------- */

  const Cart = {
    drawer: null,
    lastFocus: null,

    sectionIds() {
      const ids = ['cart-drawer'];
      const page = qs('[data-cart-page]');
      const pageSection = page?.closest('.shopify-section');
      if (pageSection) ids.push(pageSection.id.replace('shopify-section-', ''));
      return ids;
    },

    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (id === 'cart-drawer') {
          const fresh = qs('[data-cart-drawer-content]', doc);
          const current = qs('[data-cart-drawer-content]');
          if (fresh && current) current.replaceWith(fresh);
        } else {
          const target = document.getElementById(`shopify-section-${id}`);
          const fresh = doc.getElementById(`shopify-section-${id}`) || doc.body.firstElementChild;
          if (target && fresh) target.innerHTML = fresh.innerHTML;
        }
      });
    },

    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.textContent = count > 0 ? count : '';
      });
    },

    async refreshCount() {
      try {
        const res = await fetch(`${CE.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.updateCount(cart.item_count);
      } catch (err) {
        /* ignore */
      }
    },

    open() {
      if (CE.cartType !== 'drawer' || !this.drawer) {
        window.location.href = CE.routes.cart;
        return;
      }
      this.lastFocus = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      qs('.drawer__panel', this.drawer)?.focus();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      this.lastFocus?.focus?.();
    },

    async add(form) {
      const formData = new FormData(form);
      formData.append('sections', this.sectionIds().join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${CE.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || CE.strings.error);
      this.renderSections(data.sections);
      await this.refreshCount();
      return data;
    },

    async change(line, quantity) {
      const res = await fetch(`${CE.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionIds(), sections_url: window.location.pathname }),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || CE.strings.error);
      this.renderSections(data.sections);
      this.updateCount(data.item_count);
      return data;
    },

    init() {
      this.drawer = qs('[data-cart-drawer]');

      // Open drawer from header icon
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && CE.cartType === 'drawer' && this.drawer) {
          e.preventDefault();
          this.open();
        }
        if (e.target.closest('[data-drawer-close]')) this.close();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.close();
        // Keep focus inside the open drawer
        if (e.key === 'Tab' && this.drawer?.classList.contains('is-open')) {
          const focusables = qsa('a[href], button:not([disabled]), input:not([disabled]), textarea, select', this.drawer).filter(
            (el) => el.offsetParent !== null
          );
          if (!focusables.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });

      // Quick add + product form (progressive enhancement: forms work without JS)
      document.addEventListener('submit', async (e) => {
        const form = e.target;
        const isQuick = form.matches('[data-quick-add]');
        const isProduct = form.matches('[data-product-form]');
        if (!isQuick && !isProduct) return;
        if (e.submitter && e.submitter.name === 'checkout') return;
        e.preventDefault();

        const btn = qs('button[type="submit"]', form);
        const errorEl = qs('[data-form-error]', form);
        if (btn?.disabled) return;
        btn?.classList.add('is-loading');
        btn?.setAttribute('aria-disabled', 'true');
        qs('.spinner', btn)?.removeAttribute('hidden');
        if (errorEl) errorEl.textContent = '';

        try {
          await this.add(form);
          this.open();
        } catch (err) {
          if (errorEl) errorEl.textContent = err.message;
          else window.location.href = CE.routes.cart;
        } finally {
          btn?.classList.remove('is-loading');
          btn?.removeAttribute('aria-disabled');
          if (isQuick) qs('.spinner', btn)?.setAttribute('hidden', '');
        }
      });

      // Line item quantity / remove (drawer + cart page)
      const lineUpdate = async (lineEl, qty) => {
        const container = lineEl.closest('[data-cart-items]') || lineEl;
        container.classList.add('is-updating');
        try {
          await this.change(Number(lineEl.dataset.line), qty);
        } catch (err) {
          console.warn('[CasaEsmeralda] cart update failed:', err.message);
          window.location.reload();
        } finally {
          container.classList.remove('is-updating');
        }
      };

      document.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty-change]');
        const remove = e.target.closest('[data-remove]');
        const line = e.target.closest('.cart-line');
        if (!line) return;
        if (step) {
          const input = qs('[data-line-qty]', line);
          const next = Math.max(0, Number(input.value) + Number(step.dataset.qtyChange));
          const max = input.max ? Number(input.max) : Infinity;
          if (next > max) return;
          input.value = next;
          lineUpdate(line, next);
        }
        if (remove) {
          e.preventDefault();
          lineUpdate(line, 0);
        }
      });

      document.addEventListener(
        'change',
        (e) => {
          const input = e.target.closest('[data-line-qty]');
          if (!input) return;
          const line = input.closest('.cart-line');
          lineUpdate(line, Math.max(0, Number(input.value) || 0));
        },
        true
      );

      // Cart note (cart page)
      const note = qs('[data-cart-note]');
      if (note) {
        note.addEventListener(
          'input',
          debounce(() => {
            fetch(`${CE.routes.cartUpdate}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ note: note.value }),
            });
          }, 500)
        );
      }
    },
  };

  /* ---------- Product page ---------- */

  function initProduct(section) {
    const jsonEl = qs('[data-product-json]', section);
    if (!jsonEl) return;
    const data = JSON.parse(jsonEl.textContent);
    const form = qs('[data-product-form]', section);
    const idInput = qs('[data-variant-id]', section);
    const addBtn = qs('[data-add-button]', section);
    const addLabel = qs('[data-add-label]', section);
    const priceWrap = qs('[data-product-price] [data-price]', section);
    const stockNote = qs('[data-stock-note]', section);
    const fieldsets = qsa('[data-option-index]', section);

    // Gallery
    const showMedia = (mediaId) => {
      if (!mediaId) return;
      qsa('.gallery__slide', section).forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === String(mediaId)));
      qsa('[data-thumb]', section).forEach((t) => t.setAttribute('aria-current', String(t.dataset.thumb === String(mediaId))));
    };
    qsa('[data-thumb]', section).forEach((thumb) => thumb.addEventListener('click', () => showMedia(thumb.dataset.thumb)));

    // Quantity steppers
    qsa('[data-qty-step]', section).forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        input.value = Math.max(1, (Number(input.value) || 1) + Number(btn.dataset.qtyStep));
      });
    });

    if (!fieldsets.length) return;

    const selectedOptions = () =>
      fieldsets.map((fs) => qs('input:checked', fs)?.value ?? null);

    const updateAvailability = (selected) => {
      // Mark values that cannot form an available variant with the other current selections
      fieldsets.forEach((fs, index) => {
        qsa('input', fs).forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const ok = data.variants.some((v) => v.available && v.options.every((o, i) => o === candidate[i]));
          input.classList.toggle('is-unavailable', !ok);
        });
      });
    };

    const update = () => {
      const selected = selectedOptions();
      fieldsets.forEach((fs, i) => {
        const label = qs('[data-option-selected]', fs);
        if (label) label.textContent = selected[i] ?? '';
      });
      updateAvailability(selected);

      const variant = data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      if (!variant) {
        idInput.disabled = true;
        addBtn.disabled = true;
        addLabel.textContent = CE.strings.unavailable;
        if (stockNote) stockNote.textContent = '';
        return;
      }

      idInput.disabled = false;
      idInput.value = variant.id;
      addBtn.disabled = !variant.available;
      addLabel.textContent = variant.available ? CE.strings.addToCart : CE.strings.soldOut;

      if (priceWrap) {
        const onSale = variant.compare_at_price > variant.price;
        priceWrap.innerHTML =
          `<span class="price__current">${formatMoney(variant.price)}</span>` +
          (onSale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : '');
      }

      if (stockNote) {
        stockNote.textContent = variant.low_stock > 0 ? data.strings.lowStock.replace('[count]', variant.low_stock) : '';
      }

      if (variant.featured_media_id) showMedia(variant.featured_media_id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    fieldsets.forEach((fs) => fs.addEventListener('change', update));
    updateAvailability(selectedOptions());
    if (!form) return;
  }

  /* ---------- Product recommendations ---------- */

  function initRecommendations() {
    qsa('[data-recommendations]').forEach(async (el) => {
      if (el.children.length) return;
      try {
        const res = await fetch(el.dataset.url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = qs('[data-recommendations]', doc);
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      } catch (err) {
        /* recommendations are optional */
      }
    });
  }

  /* ---------- Collection filters & sorting ---------- */

  function initFacets() {
    const submit = (form) => {
      const params = new URLSearchParams(new FormData(form));
      // drop empty values (e.g. blank price inputs)
      for (const [k, v] of Array.from(params.entries())) if (v === '') params.delete(k);
      window.location.search = params.toString();
    };

    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-auto-submit]')) {
        const form = e.target.form;
        if (form) submit(form);
      }
    });

    const debounced = debounce((form) => submit(form), 800);
    document.addEventListener('input', (e) => {
      if (e.target.matches('[data-auto-submit-debounce]') && e.target.form) debounced(e.target.form);
    });

    qsa('[data-facets-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.getElementById(btn.getAttribute('aria-controls'));
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        target?.classList.toggle('is-open', !open);
      });
    });
  }

  /* ---------- Booking ---------- */

  function initBooking() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-book-service]');
      if (!btn) return;
      const target = document.querySelector(btn.getAttribute('href'));
      const select = target ? qs('[data-service-select]', target) : null;
      if (select) {
        const option = Array.from(select.options).find((o) => o.value === btn.dataset.bookService);
        if (option) select.value = option.value;
      }
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setTimeout(() => qs('input, select', target)?.focus({ preventScroll: true }), 500);
      }
    });
  }

  /* ---------- Customer login ---------- */

  function initLogin() {
    const recover = qs('[data-recover]');
    const login = qs('[data-login]');
    if (!recover || !login) return;
    const show = (showRecover) => {
      recover.style.display = showRecover ? '' : 'none';
      login.style.display = showRecover ? 'none' : '';
    };
    if (window.location.hash === '#recover' || qs('[data-recover-error]', recover)) show(true);
    qsa('[data-recover-toggle]').forEach((a) =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        show(a.getAttribute('href') === '#recover');
      })
    );
  }

  /* ---------- Boot ---------- */

  function boot() {
    initPalette();
    initHeader();
    initTabs();
    Cart.init();
    qsa('[data-product-section]').forEach(initProduct);
    initRecommendations();
    initFacets();
    initBooking();
    initLogin();

    const autofocus = qs('[data-autofocus]');
    if (autofocus) {
      autofocus.scrollIntoView({ block: 'center' });
      autofocus.focus({ preventScroll: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    initTabs(e.target);
    qsa('[data-product-section]', e.target).forEach(initProduct);
    initRecommendations();
    if (qs('[data-header]', e.target)) initHeader();
  });
})();
